import React, { createContext, useContext, useState, useCallback } from 'react';
import axiosInstance from '../config/axios';
import { v4 as uuidv4 } from 'uuid';

const KidsFlasherContext = createContext();

export const useKidsFlasher = () => useContext(KidsFlasherContext);

export const KidsFlasherProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [playerData, setPlayerData] = useState(null);
  const [error, setError] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  
  const validateRiotId = (riotId) => {
    const regex = /^.+#.+$/;
    return regex.test(riotId);
  };
  
  const fetchPlayerData = useCallback(async (riotId) => {
    if (!validateRiotId(riotId)) {
      setError('Invalid Riot ID format. Please use Username#Tag format.');
      return { status: 'error', error: 'Invalid Riot ID format' };
    }
    
    try {
      setLoading(true);
      setError(null);
      setPlayerData(null);
      
      const response = await axiosInstance.get(`/player/${encodeURIComponent(riotId)}`);
      console.log('Initial API response:', response.data);
      
      if (response.data.status === 'completed' && response.data.data) {
        console.log('Got cached data:', response.data.data);
        setPlayerData(response.data.data);
        setQueueStatus(null);
        return { status: 'completed', data: response.data.data };
      }
      
      if (response.data.status === 'queued') {
        const queueData = {
          requestId: response.data.requestId,
          position: response.data.position,
          riotId: riotId
        };
        console.log('Setting queue status:', queueData);
        setQueueStatus(queueData);
        return { status: 'queued', requestId: response.data.requestId };
      }
      
      throw new Error('Invalid response from server');
      
    } catch (err) {
      const errorMessage = err.response?.status === 504
        ? 'Request is taking longer than expected. Please wait...'
        : err.response?.data?.error || 'Failed to fetch player data. Please try again.';
      
      setError(errorMessage);
      return { status: 'error', error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);
  
  const pollQueueStatus = useCallback(async (requestId) => {
    if (!requestId) {
      console.error('No requestId provided for polling');
      return false;
    }
    
    try {
      console.log('Polling queue status for requestId:', requestId);
      const response = await axiosInstance.get(`/queue/${requestId}`);
      console.log('Poll response:', response.data);
      
      if (response.data.status === 'completed' && response.data.data) {
        console.log('Request completed');
        setPlayerData(response.data.data);
        setQueueStatus(null);
        return true;
      }
      
      if (response.data.status === 'not_found') {
        console.log('Request not found or expired');
        setError('Request expired or failed. Please try again.');
        setQueueStatus(null);
        return 'expired';
      }
      
      if (response.data.status === 'error') {
        console.log('Request error');
        setError('An error occurred while processing your request. Please try again.');
        setQueueStatus(null);
        return false;
      }
      
      if (response.data.status === 'queued') {
        const position = response.data.position;
        if (position >= -2) {
          console.log('Updating queue position:', position);
          setQueueStatus(prev => ({
            ...prev,
            position: position,
            puuid: response.data.puuid
          }));
        }
        // Return the response data so the LoadingPage can access progress info
        return response.data;
      }
      
      return false;
    } catch (error) {
      console.error('Poll error:', error);
      
      if (error.response?.status !== 504) {
        setError('Failed to check request status. Please try again.');
        setQueueStatus(null);
      }
      return false;
    }
  }, []);
  
  const generateShareLink = useCallback(async () => {
    if (!playerData) return null;
    
    try {
      const shareId = uuidv4();
      await axiosInstance.post(`/share`, {
        shareId,
        playerData
      });
      
      return shareId;
    } catch (err) {
      setError('Failed to generate share link. Please try again.');
      return null;
    }
  }, [playerData]);
  
  const fetchSharedData = useCallback(async (shareId) => {
    if (loading) return false;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get(`/share/${shareId}`);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      setPlayerData(response.data);
      return true;
    } catch (err) {
      console.error('Error fetching shared data:', err);
      setError(err.response?.data?.error || 'Failed to load shared data. The link may be invalid or expired.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [loading]);
  
  const value = {
    loading,
    playerData,
    error,
    queueStatus,
    validateRiotId,
    fetchPlayerData,
    pollQueueStatus,
    generateShareLink,
    fetchSharedData,
    setError
  };
  
  return (
    <KidsFlasherContext.Provider value={value}>
      {children}
    </KidsFlasherContext.Provider>
  );
};